import { Link } from "react-router-dom";
import { SITE } from "../../mocks/siteData";
import footerContent from "@/content/footer.json";

interface FooterSocialLink {
  label: string;
  url?: string;
  enabled?: boolean;
}

const SOCIAL_ICON_RULES = [
  { matches: ["facebook.com", "fb.com"], icon: "ri-facebook-fill" },
  { matches: ["instagram.com"], icon: "ri-instagram-line" },
  { matches: ["wa.me", "whatsapp.com"], icon: "ri-whatsapp-line" },
  { matches: ["x.com", "twitter.com"], icon: "ri-twitter-x-line" },
  { matches: ["linkedin.com"], icon: "ri-linkedin-fill" },
  { matches: ["youtube.com", "youtu.be"], icon: "ri-youtube-fill" },
  { matches: ["tiktok.com"], icon: "ri-tiktok-fill" },
];

const isValidSocialUrl = (url?: string) => {
  if (!url?.trim()) return false;

  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
};

const getSocialIcon = (link: FooterSocialLink) => {
  const searchable = `${link.url ?? ""} ${link.label ?? ""}`.toLowerCase();
  return SOCIAL_ICON_RULES.find((rule) =>
    rule.matches.some((match) => searchable.includes(match))
  )?.icon ?? "ri-links-line";
};

export default function Footer() {
  const socialLinks = ((footerContent.socialLinks ?? []) as FooterSocialLink[]).filter(
    (link) => link.enabled === true && isValidSocialUrl(link.url)
  );

  return (
    <footer>
      {/* Pre-footer trust band */}
      <div
        className="py-16 lg:py-20"
        style={{
          background: "linear-gradient(135deg, #C4902A 0%, #b07e20 60%, #8a6118 100%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-center">
            <div>
              <p className="text-white/65 text-xs font-semibold tracking-[0.2em] uppercase mb-2">{footerContent.preFooter.eyebrow}</p>
              <h3
                className="text-white font-serif text-2xl lg:text-3xl leading-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {footerContent.preFooter.headline}
              </h3>
            </div>
            <div className="grid grid-cols-3 gap-6 text-center">
              {footerContent.preFooter.stats.map((s) => (
                <div key={s.lbl}>
                  <p
                    className="text-white font-serif font-bold text-3xl leading-none mb-1"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {s.val}
                  </p>
                  <p className="text-white/60 text-[11px] tracking-wide uppercase">{s.lbl}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 lg:items-end">
              <a
                href={`tel:${SITE.phonePlain}`}
                className="inline-flex items-center justify-center gap-2 bg-white text-[#C4902A] font-bold px-8 py-3.5 rounded-full hover:bg-white/90 transition-colors whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill text-base"></i>
                {SITE.phone}
              </a>
              <Link
                to={footerContent.preFooter.bookHref}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 hover:border-white/70 transition-colors whitespace-nowrap text-sm"
              >
                {footerContent.preFooter.bookLabel}
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div
        className="text-white pt-16 pb-0"
        style={{ background: "linear-gradient(180deg, #0A0A0A 0%, #141414 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 pb-14 border-b border-white/[0.07]">
            {/* Brand */}
            <div className="lg:col-span-1">
              <img
                src={SITE.logoFooter}
                alt={SITE.name}
                className="h-12 w-auto object-contain mb-5"
                style={{ mixBlendMode: "screen" }}
              />
              <p className="text-white/40 text-sm leading-relaxed mb-6">
                {footerContent.description}
              </p>
              {socialLinks.length > 0 && (
                <div className="flex items-center gap-3">
                  {socialLinks.map((link) => {
                    const icon = getSocialIcon(link);
                    return (
                      <a
                        key={`${link.label}-${link.url}`}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 flex items-center justify-center rounded-full border border-white/15 text-white/45 hover:text-white hover:border-white/40 transition-colors"
                        aria-label={link.label}
                      >
                        <i className={`${icon} text-sm`}></i>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Services */}
            <div>
              <h4 className="text-white font-semibold text-xs tracking-[0.2em] uppercase mb-6">
                {footerContent.servicesTitle}
              </h4>
              <ul className="space-y-3">
                {footerContent.servicesLinks.map((s) => (
                  <li key={s.href}>
                    <Link
                      to={s.href}
                      className="text-white/40 text-sm hover:text-[#C4902A] transition-colors leading-relaxed"
                    >
                      {s.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold text-xs tracking-[0.2em] uppercase mb-6">
                {footerContent.quickLinksTitle}
              </h4>
              <ul className="space-y-3">
                {footerContent.quickLinks.map((l) => (
                  <li key={l.href}>
                    <Link
                      to={l.href}
                      className="text-white/40 text-sm hover:text-[#C4902A] transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold text-xs tracking-[0.2em] uppercase mb-6">
                {footerContent.contactTitle}
              </h4>
              <ul className="space-y-4">
                <li>
                  <a href={`tel:${SITE.phonePlain}`} className="flex items-start gap-3 text-white/40 hover:text-white text-sm transition-colors group">
                    <i className="ri-phone-line mt-0.5 text-[#C4902A] shrink-0"></i>
                    <span>{SITE.phone}</span>
                  </a>
                </li>
                <li>
                  <a href={`mailto:${SITE.email}`} className="flex items-start gap-3 text-white/40 hover:text-white text-sm transition-colors">
                    <i className="ri-mail-line mt-0.5 text-[#C4902A] shrink-0"></i>
                    <span>{SITE.email}</span>
                  </a>
                </li>
                <li>
                  <a href={SITE.mapUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-white/40 hover:text-white text-sm transition-colors">
                    <i className="ri-map-pin-line mt-0.5 text-[#C4902A] shrink-0"></i>
                    <span>{SITE.address}</span>
                  </a>
                </li>
                <li className="flex items-start gap-3 text-white/40 text-sm">
                  <i className="ri-time-line mt-0.5 text-[#C4902A] shrink-0"></i>
                  <div>
                    <p>{SITE.hours.weekday}</p>
                    <p>{SITE.hours.weekend}</p>
                    <p className="text-[#C4902A] font-medium">{SITE.hours.emergency}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-white/20 text-xs">
            <p>
              {footerContent.copyrightText.replace("{year}", String(new Date().getFullYear()))}
            </p>
            <div className="flex gap-5">
              {footerContent.legalLinks.map((link) => (
                <span key={link.label} className="hover:text-white/50 transition-colors cursor-pointer">{link.label}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
